import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'positiveAndNegativeDoubleIndicatorSumBar',
  name_en: 'Positive and Negative Double Indicator Sum Bar',
  // name_zh: '正负双指标求和条形图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR, DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y2: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    y2: string;
  };
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  // eslint-disable-next-line max-lines-per-function
  render(api, spec, $dom) {
    const { data: dataArray, util, hook, token } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, y2 }
    } = spec;
    const data = dataArray[dataIndex];
    const presetOption = hook.buildStandardChartPresetOption(api);

    // * -------------------------- data
    const dataMetaInfo = api.hook.parseDataMetaInfo(data);
    const { yAxisData } = util.dataEncoding.encoding(data.values.slice(), {
      yAxisData: {
        field: x,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      }
    });

    // * -------------------------- chart
    const chartViewRect = $dom.getBoundingClientRect();
    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = {
      ...baseGridOption
    };
    const showBarNum = 8;
    const barWidth = (chartViewRect.height - grid.top - grid.bottom) / yAxisData.length / 2;
    const barMaxWidth = util.const.BAR_MAX_WIDTH;
    const showNum = barWidth >= 12 / 0.8;

    return {
      grid: {
        ...grid,
        right: !showNum ? 24 : grid.right
      },
      dataZoom: !showNum
        ? [
            {
              type: 'slider',
              show: !showNum,
              orient: 'vertical',
              width: 8,
              startValue: yAxisData.length - 1 - (showBarNum - 1),
              endValue: yAxisData.length - 1,
              handleSize: 0,
              fillerColor:
                token?.dvStandardChart?.baseOption?.dataZoom[0]?.dvScrollColor || '#DADDE0',
              borderColor: 'transparent',
              borderRadius: 0,
              showDetail: false,
              showDataShadow: false,
              zoomLock: true,
              brushSelect: false
            }
          ]
        : [
            {
              ...presetOption.dataZoom[0],
              orient: 'vertical'
            }
          ],
      tooltip: {
        trigger: 'axis'
      },
      xAxis: {
        show: true,
        type: 'value',
        z: undefined
      },
      yAxis: {
        show: true,
        type: 'category',
        dvNameLocation: undefined,
        name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
        nameTextStyle: {
          padding: [-8, 0, 0, 4]
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          showMinLabel: true,
          showMaxLabel: true,
          hideOverlap: true
        },
        splitLine: {
          show: false
        },
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            dvMaxWidth:
              barWidth > barMaxWidth ? barMaxWidth * util.const.SHADOW_WIDTH_RATIO * 2 : undefined
          },
          z: 0
        },
        data: yAxisData
      },
      series: [
        {
          name: 'all',
          type: 'bar',
          barMaxWidth: barWidth > barMaxWidth ? barMaxWidth : undefined,
          emphasis: {
            focus: 'series'
          },
          data: yAxisData.map(date => {
            const dataItem = data.values.find(item => item[x] === date);

            if (!dataItem) {
              return null;
            }

            const value = +dataItem[y] + -dataItem[y2];
            const result = hook.formatDataNumberValue(value, {
              type: 'table'
            });
            const isPositive = result.value < 0;
            return {
              value,
              label: {
                show: showNum,
                position: isPositive ? 'insideRight' : 'insideLeft',
                padding: isPositive ? [0, 8, 0, 0] : [0, 0, 0, 8],
                formatter: () => {
                  return result.value + result.unit;
                }
              }
            };
          })
        },
        {
          name: y,
          type: 'bar',
          stack: 'Total',
          barMaxWidth: barWidth > barMaxWidth ? barMaxWidth : undefined,
          label: {
            show: showNum,
            position: 'insideLeft',
            padding: [0, 0, 0, 8],
            formatter: params => {
              const result = hook.formatDataNumberValue(params.data, {
                type: 'table',
                unit: dataMetaInfo[y]?.unit
              });
              return result.value + result.unit;
            }
          },
          emphasis: {
            focus: 'series'
          },
          data: yAxisData.map(date => {
            const dataItem = data.values.find(item => item[x] === date);

            return dataItem ? +dataItem[y] : null;
          })
        },
        {
          name: y2,
          type: 'bar',
          stack: 'Total',
          barMaxWidth: barWidth > barMaxWidth ? barMaxWidth : undefined,
          label: {
            show: showNum,
            position: 'insideRight',
            padding: [0, 8, 0, 0],
            formatter: params => {
              const result = hook.formatDataNumberValue(params.data, {
                type: 'table',
                unit: dataMetaInfo[y2]?.unit
              });
              return result.value + result.unit;
            }
          },
          emphasis: {
            focus: 'series'
          },
          data: yAxisData.map(date => {
            const dataItem = data.values.find(item => item[x] === date);

            return dataItem ? -dataItem[y2] : null;
          })
        }
      ]
    };
  }
});
